import { Component, OnDestroy, AfterContentInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { finalize, map, switchMap, distinctUntilChanged, debounceTime } from 'rxjs/operators';

import { untilDestroyed } from '@app/core';
import { environment } from '@env/environment';
import { AuthenticationMethod } from '@app/models';
import { AppService, AuthenticationService, ErrorHandlerService, MfaService, NotyService, OAuthService, PhoneOtpProviderService, PhoneOtpProvider, UserService } from '@app/services';
import { CountdownTimerService } from '@app/services/countdowntimer.service';
import { capitalize } from 'lodash';

enum LoginStepEnum {
  Login = 'l',
  Password = 'p',
  SmsCode = 'sms',
  WhatsappCode = 'whats',
  OtpCode = 'otp',
  MfaSelect = 'mfa',
  RecoveryCode = 'rc',
  Register = 'r'
}

@Component({
  selector: 'app-auth-login',
  templateUrl: './auth-login.component.html',
  styleUrls: ['../auth.scss', './auth-login.component.scss'],
  standalone: false
})
export class AuthLoginComponent implements AfterContentInit, OnDestroy {
  form!: UntypedFormGroup;
  isLoading = false;
  isSmsTimeout = false;
  loginMessageTime = 20000;
  loginStep = LoginStepEnum.Login;
  formattedTime = '';
  loginByMail = true;
  isLoginProgressDisabled$: Observable<boolean>;
  isSocialEmailAlreadyRegistered = false;
  socialEmailProvider = '';
  currentPhoneOtpProvider!: PhoneOtpProvider;

  readonly AuthenticationMethod = AuthenticationMethod;
  readonly LoginStepEnum = LoginStepEnum;
  readonly rawLoginSteps = [LoginStepEnum.Login, LoginStepEnum.Password, LoginStepEnum.Register, LoginStepEnum.SmsCode, LoginStepEnum.WhatsappCode];
  readonly mfaOptions = [{ type: 'totp', description: 'auth.mfaSelectStep.options.totp' }];

  constructor(
    public route: ActivatedRoute,
    public timerService: CountdownTimerService,
    public translateService: TranslateService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private appService: AppService,
    private authenticationService: AuthenticationService,
    private notyService: NotyService,
    private oauthService: OAuthService,
    private userService: UserService,
    private errorHandlerService: ErrorHandlerService,
    public phoneOtpProvider: PhoneOtpProviderService,
    private mfaService: MfaService
  ) {
    this.appService.loadStatusPageWidget();
  }

  ngOnDestroy() {}

  ngAfterContentInit() {
    this.restartLogin();
    this.buildLoginProgressDisable();

    if (this.form.get('email').value) {
      this.checkLoginStep(this.form.get('email').value);
    }
  }

  checkLoginStep(emailOrPhoneInput: string) {
    let loginType: string | null = null;
    if (this.loginStep === LoginStepEnum.Login && this.isValidEmail(emailOrPhoneInput)) {
      loginType = 'email';
    } else if (!this.loginByMail && this.isValidInternationalPhone(emailOrPhoneInput)) {
      loginType = 'phone';
    }

    this.form.get(this.loginByMail ? 'email' : 'phone').markAsTouched();

    if (!loginType || (!this.loginByMail && !this.isValidInternationalPhone(emailOrPhoneInput)) || (this.loginByMail && !this.isValidEmail(emailOrPhoneInput))) {
      return;
    }

    this.isLoading = true;
    this.authenticationService
      .userExists({ [loginType]: emailOrPhoneInput })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: userExists => {
          if (this.loginByMail && this.isValidEmail(emailOrPhoneInput)) {
            this.changeLoginStep(userExists ? LoginStepEnum.Password : LoginStepEnum.Register);
            if (userExists) {
              setTimeout(() => (document.querySelector('input[name=password]') as HTMLInputElement)?.focus());
            }
          } else if (userExists) {
            this.sendPhoneCode(emailOrPhoneInput, true);
            this.timeOutForAlternativeVerification();
          } else if (!userExists && loginType === 'phone') {
            this.form.get('phone')?.setErrors({ userExistsError: true });
          }
        },
        error: error => {
          if (error?.message?.includes('social_already_registered')) {
            this.socialEmailProvider = capitalize(error.message.replace('social_already_registered_', ''));
            this.isSocialEmailAlreadyRegistered = true;
          }
        }
      });
  }

  login(method: AuthenticationMethod = AuthenticationMethod.Email, additionalInfo?: any) {
    this.isLoading = true;
    if (method === AuthenticationMethod.Email || this.phoneOtpProvider.isPhoneAuthentication(method)) {
      ['email', 'phone', 'password', 'phoneCode'].forEach(field => this.form.get(field)?.markAsTouched());
    }

    this.oauthService
      .login(method, { ...(additionalInfo || {}), stopAutoLogin: true })
      .pipe(
        switchMap(oauth => {
          if (oauth) {
            return this.authenticationService.login({ code: oauth.accessToken, phone_number: oauth.phoneNumber, referral: this.form.get('referral').value }, method);
          } else {
            return this.authenticationService.loginEmail({ email: this.form.get('email').value, password: this.form.get('password').value });
          }
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: credentials => {
          if (credentials.mfa.length > 0) {
            this.changeLoginStep(LoginStepEnum.OtpCode);
          } else {
            this.loggedInNavigation();
          }
        },
        error: error => {
          if (error?.status === 424) {
            const redirect = this.route.snapshot.queryParams.redirect;
            this.router.navigate(['/confirmacao'], { queryParams: { email: this.form.get('email').value, ...(redirect ? { redirect } : {}) } });
          } else if (error?.status === 422 && this.phoneOtpProvider.isPhoneAuthentication(method)) {
            // Gambiarra pra transformar erro { message: 'token_invalid' } do back em algo que o front consiga usar pra mostrar erro no campo de código
            this.form.get('phoneCode').setErrors({ server: this.translateService.instant('error_phone_code_invalid') });
          } else {
            this.errorHandlerService.handleValidation(this.form, error);
          }
        }
      });
  }

  sendPhoneCode(phone: string, notify: boolean = false, provider?: PhoneOtpProvider) {
    this.currentPhoneOtpProvider = this.phoneOtpProvider.normalizeProviderForPhone(phone, provider);
    this.isLoading = true;
    this.isSmsTimeout = true;
    this.phoneOtpProvider
      .sendVerification(phone, false, this.currentPhoneOtpProvider)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.changeLoginStep(this.phoneOtpLoginStep());
          setTimeout(() => (document.querySelector('input[name=phoneCode]') as HTMLInputElement).focus());
          setTimeout(() => (this.isSmsTimeout = false), this.loginMessageTime);
          if (notify) {
            this.notyService.success(this.translateService.instant('notyService.confirmationCodeResentSuccess'));
          }
        },
        error: error => {
          this.isSmsTimeout = false;
          this.errorHandlerService.handle(error);
        }
      });
  }

  sendFallbackPhoneCode(phone: string) {
    const fallbackProvider = this.phoneOtpProvider.fallbackProvider(this.currentPhoneOtpProvider);

    if (fallbackProvider) {
      this.sendPhoneCode(phone, true, fallbackProvider);
    }
  }

  submit() {
    if (this.loginStep === LoginStepEnum.Login) {
      const inputValue = this.loginByMail ? this.form.get('email').value : this.form.get('phone').value;
      this.checkLoginStep(inputValue);
    } else if ([LoginStepEnum.Password, LoginStepEnum.SmsCode, LoginStepEnum.WhatsappCode].includes(this.loginStep)) {
      const method = this.loginStep === LoginStepEnum.Password ? AuthenticationMethod.Email : this.phoneOtpProvider.authenticationMethod(this.currentPhoneOtpProvider);
      const additionalInfo = this.isValidEmail(this.form.get('email').value) ? null : { phoneNumber: this.form.get('phone').value, phoneCode: this.form.get('phoneCode').value };
      this.login(method, additionalInfo);
    } else if (this.loginStep === LoginStepEnum.OtpCode) {
      this.verifyTOTP();
    } else if (this.loginStep === LoginStepEnum.MfaSelect) {
      this.changeLoginStep(LoginStepEnum.OtpCode); // Only a single select option for now, so it's statically set to TOTP
    } else if (this.loginStep === LoginStepEnum.RecoveryCode) {
      this.disableTOTP();
    }
  }

  back() {
    this.loginStep =
      (
        {
          [LoginStepEnum.MfaSelect]: LoginStepEnum.OtpCode,
          [LoginStepEnum.RecoveryCode]: LoginStepEnum.MfaSelect
        } as Record<LoginStepEnum, LoginStepEnum>
      )[this.loginStep] || LoginStepEnum.Login;
  }

  verifyTOTP() {
    const code = this.form.get('otp');
    code.markAsTouched();
    if (code.value.length < 6) {
      code.setErrors({ server: this.translateService.instant('error_otp_required') });
      return;
    }
    this.isLoading = true;
    this.mfaService
      .verifyTOTP({ otp: code.value })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => this.loggedInNavigation(),
        error: error => {
          code.setErrors({ server: this.translateService.instant('error_' + error.message) });
        }
      });
  }

  disableTOTP() {
    this.isLoading = true;
    this.form.get('recoveryCode').markAsTouched();
    const recoveryCode = (this.form.get('recoveryCode').value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    this.mfaService
      .disableTOTP({ recoveryCode })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: () => {
          this.notyService.success(this.translateService.instant('notyService.totpDisabled'));
          this.restartLogin();
        },
        error: error => {
          const handledError = this.errorHandlerService.handle(error, { ignoreNotificationOn: ['totp_recovery_code_invalid'] });
          this.form.get('recoveryCode').setErrors({ server: this.translateService.instant('error_' + handledError.message) });
        }
      });
  }

  restartLogin() {
    this.authenticationService.logout().subscribe();

    this.isLoading = false;
    this.isSmsTimeout = false;
    this.loginStep = LoginStepEnum.Login;
    this.formattedTime = '';
    this.loginByMail = true;
    this.currentPhoneOtpProvider = this.phoneOtpProvider.primaryProvider;

    const formValues = {
      email: this.route.snapshot.queryParams.email || '',
      phone: '',
      password: '',
      phoneCode: '',
      otp: '',
      mfaType: this.mfaOptions[0].type,
      recoveryCode: '',
      referral: this.route.snapshot.queryParams.r || null
    };
    if (this.form) {
      setTimeout(() => this.form.setValue(formValues));
    } else {
      this.form = this.formBuilder.group(formValues);
    }
  }

  isValidEmail(str: string) {
    return !!(str || '').match(/@/);
  }

  isValidInternationalPhone(str: string): boolean {
    const phonePattern = /^\+?[1-9]\d{1,14}$/;
    return phonePattern.test(str);
  }

  isRawLogin() {
    return this.rawLoginSteps.includes(this.loginStep);
  }

  shouldShowOauth() {
    return !environment.electron && this.appService.isAutentiqueDomain() && this.isRawLogin();
  }

  timeOutForAlternativeVerification() {
    if (this.timerService.isCountdownInProgress()) {
      this.timerService.stopCountdown();
    }
    this.timerService
      .startCountdownTimer()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: time => (this.formattedTime = time)
      });
  }

  buildLoginProgressDisable() {
    this.isLoginProgressDisabled$ =
      this.isLoginProgressDisabled$ ||
      this.form.valueChanges.pipe(
        debounceTime(0),
        map(
          () =>
            this.isLoading ||
            this.loginStep === LoginStepEnum.Register ||
            (this.loginStep === LoginStepEnum.SmsCode && this.form.get('phoneCode').invalid) ||
            (this.loginStep === LoginStepEnum.Password && this.form.get('password').invalid) ||
            ([LoginStepEnum.SmsCode, LoginStepEnum.WhatsappCode].includes(this.loginStep) && this.form.get('phoneCode').invalid) ||
            (this.loginStep === LoginStepEnum.OtpCode && this.form.get('otp').invalid) ||
            (this.loginStep === LoginStepEnum.RecoveryCode && this.form.get('recoveryCode').invalid)
        ),
        distinctUntilChanged()
      );
  }

  changeLoginStep(step: LoginStepEnum) {
    this.loginStep = step;
    this.form.updateValueAndValidity({ emitEvent: true });
  }

  navigateToRegister() {
    const redirect = this.route.snapshot.queryParams.redirect;
    this.router.navigate(['/cadastro'], {
      queryParams: {
        ...(this.isValidEmail(this.form.get('email').value) ? { email: this.form.get('email').value } : {}),
        ...(redirect ? { redirect } : {})
      }
    });
  }

  navigateToRecoveryPassword() {
    const email = this.form.get('email').value;
    this.router.navigate(['/recuperar-senha'], { state: { fromAuthRegister: true }, queryParams: { ...(this.isValidEmail(email) ? { email } : {}) } });
  }

  hasPhoneOtpFallbackProvider() {
    return this.phoneOtpProvider.hasFallbackProvider(this.currentPhoneOtpProvider);
  }

  phoneOtpFallbackProviderLabel() {
    const fallbackProvider = this.phoneOtpProvider.fallbackProvider(this.currentPhoneOtpProvider);
    return fallbackProvider ? this.phoneOtpProvider.providerLabel(fallbackProvider) : '';
  }

  private phoneOtpLoginStep(): LoginStepEnum {
    return this.currentPhoneOtpProvider === 'whatsapp' ? LoginStepEnum.WhatsappCode : LoginStepEnum.SmsCode;
  }

  private loggedInNavigation() {
    this.isLoading = true;
    this.userService
      .getCurrentUser({ fetchPolicy: 'network-only' })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: user => {
          const redirectUrl = this.route.snapshot.queryParams.redirect || '/';

          if (user.name) {
            this.appService.navigateAfterAuth(redirectUrl, this.router);
          } else {
            this.router.navigate(['/onboarding'], { state: { fromAuthRegister: true, redirectUrl: redirectUrl !== '/' ? redirectUrl : null } });
          }
        }
      });
  }
}
